/**
 * useFiscalProfile — data layer do perfil fiscal (NFS-e) da empresa ativa.
 *
 * O perfil é 1:1 com a empresa (fiscal_profiles.company_id). RLS restringe a
 * owner/admin; o servidor é a autoridade final — este hook só molda a UI e a
 * máquina de onboarding (100 → 101 → 102 → 200).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type {
  FiscalProfile,
  FiscalProfileUpdate,
  Municipality,
  TaxpayerType,
} from '@/types/fiscal';

type SaveFields = FiscalProfileUpdate & { taxpayer_type?: TaxpayerType };

export function useFiscalProfile() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['fiscal-profile', companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<FiscalProfile | null> => {
      const { data, error } = await supabase
        .from('fiscal_profiles')
        .select('*')
        .eq('company_id', companyId as string)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  const save = useMutation({
    mutationFn: async (fields: SaveFields): Promise<FiscalProfile> => {
      if (!companyId) throw new Error('Nenhuma empresa ativa');

      // Já existe → UPDATE (evita o problema de NOT NULL no upsert parcial).
      if (query.data) {
        const { data, error } = await supabase
          .from('fiscal_profiles')
          .update(fields)
          .eq('company_id', companyId)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }

      // Primeira gravação → INSERT (taxpayer_type é NOT NULL).
      const taxpayerType = fields.taxpayer_type;
      if (!taxpayerType) throw new Error('taxpayer_type é obrigatório na criação do perfil');
      const { data, error } = await supabase
        .from('fiscal_profiles')
        .insert({ ...fields, company_id: companyId, taxpayer_type: taxpayerType })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['fiscal-profile', companyId], data);
    },
  });

  return {
    companyId,
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    save,
  };
}

/** Municípios disponíveis (referência; hoje SP + Fortaleza). */
export function useMunicipalities() {
  return useQuery({
    queryKey: ['fiscal-municipalities'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Municipality[]> => {
      const { data, error } = await supabase
        .from('municipalities')
        .select('*')
        .neq('ibge', '0000000') // exclui a linha-template do Trilho Nacional
        .order('nome');
      if (error) throw error;
      return data ?? [];
    },
  });
}
